import React from "react";
import { SimpleGrid } from "@chakra-ui/react";
import NetworkMetricCard from "./NetworkMetricCard";
import { formatNumber } from "../networkReportFormatters";

const measuredOrganizations = (organizations = []) => organizations.filter(
  (organization) =>
    !["not_started", "no_survey"].includes(organization.status) &&
    Number.isFinite(organization.overallStabilityIndex)
);

export const buildNetworkFactorHighlights = (factors = [], organizations = []) => {
  const rankedFactors = factors.filter((factor) => Number.isFinite(factor.stabilityIndex));
  const strongestFactor = rankedFactors.reduce(
    (strongest, factor) => !strongest || factor.rank < strongest.rank ? factor : strongest,
    null
  );

  const developmentCounts = new Map();
  organizations.forEach((organization) => {
    if (["not_started", "no_survey"].includes(organization.status)) return;
    const seen = new Set();
    (organization.developmentAreas || []).forEach((factor) => {
      const key = factor.id ?? factor.name?.trim().toLocaleLowerCase();
      if (key === undefined || key === null || seen.has(key)) return;
      seen.add(key);
      const existing = developmentCounts.get(key);
      developmentCounts.set(key, {
        factor,
        count: (existing?.count || 0) + 1,
      });
    });
  });
  const sharedDevelopment = [...developmentCounts.values()].reduce(
    (shared, item) => !shared || item.count > shared.count ? item : shared,
    null
  );
  const networkFactor = sharedDevelopment
    ? factors.find((factor) =>
      (sharedDevelopment.factor.id != null && factor.id === sharedDevelopment.factor.id) ||
      factor.name.trim().toLocaleLowerCase() === sharedDevelopment.factor.name.trim().toLocaleLowerCase()
    )
    : null;

  const measured = measuredOrganizations(organizations);
  const highestSchool = measured.reduce(
    (highest, school) => !highest || school.overallStabilityIndex > highest.overallStabilityIndex ? school : highest,
    null
  );
  const lowestSchool = measured.reduce(
    (lowest, school) => !lowest || school.overallStabilityIndex < lowest.overallStabilityIndex ? school : lowest,
    null
  );

  return {
    strongestFactor,
    sharedDevelopment: sharedDevelopment ? { ...sharedDevelopment, networkFactor } : null,
    spread: highestSchool && lowestSchool ? {
      value: highestSchool.overallStabilityIndex - lowestSchool.overallStabilityIndex,
      highestSchool,
      lowestSchool,
    } : null,
  };
};

const NetworkFactorHighlights = ({ factors = [], organizations = [] }) => {
  const highlights = buildNetworkFactorHighlights(factors, organizations);
  const strongest = highlights.strongestFactor;
  const shared = highlights.sharedDevelopment;
  const spread = highlights.spread;

  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
      <NetworkMetricCard
        label="Strongest Factor"
        value={strongest?.name || "N/A"}
        helpText={strongest
          ? `${strongest.areaLabel} · Stability ${formatNumber(strongest.stabilityIndex)}`
          : "No measured factor data available"}
      />
      <NetworkMetricCard
        label="Most Shared Development Need"
        value={shared?.factor.name || "N/A"}
        helpText={shared
          ? `${shared.count} School${shared.count === 1 ? "" : "s"}${Number.isFinite(shared.networkFactor?.stabilityIndex) ? ` · Network stability ${formatNumber(shared.networkFactor.stabilityIndex)}` : ""}`
          : "No School development data available"}
      />
      <NetworkMetricCard
        label="Spread Between Schools"
        value={spread ? formatNumber(spread.value) : "N/A"}
        helpText={spread
          ? `Highest: ${spread.highestSchool.organizationName} (${formatNumber(spread.highestSchool.overallStabilityIndex)}) · Lowest: ${spread.lowestSchool.organizationName} (${formatNumber(spread.lowestSchool.overallStabilityIndex)})`
          : "No measured School stability data available"}
      />
    </SimpleGrid>
  );
};

export default NetworkFactorHighlights;
